import * as t from "io-ts";

import { fold } from "fp-ts/Either";
import { PathReporter } from "io-ts/PathReporter";

import selectHost from "./select-host";
import { isYantrixApiError, formatYantrixApiError } from "./api-response";
import formatIoTsErrors from "./format-io-ts-errors";

// export type PureNetworkQuery<Response> = (
//   pathname: string,
//   service?: string,
//   options?: RequestInit,
//   key?: string
// ) => Promise<Response>;

// const exactDecode = <A>(codec: t.Type<A>, data: unknown) => {
//   const exactCodec = t.exact(codec);
//   return exactCodec.decode(data);
// };

// Helper function to check if a codec is an array type
const isArrayCodec = (codec: any): codec is t.ArrayType<any> => {
  return (codec as t.ArrayType<any>).type !== undefined;
};

// // Wrapper decode function that applies t.exact dynamically
const exactDecode = <T>(codec: any, data: unknown): any => {
  const clean = (d: any, c: any): any =>
    typeof d === "object" && d !== null && "props" in c
      ? Object.fromEntries(Object.entries(d).filter(([k]) => k in c.props))
      : d;

  // const clean = (d: any, c: any): any =>{
  //   if (Array.isArray(d)) {
  //     return d.map((item) => clean(item, c.type));
  //   } else if (typeof d === 'object' && d !== null) {
  //     const validKeys = 'props' in c ? Object.keys(c.props) : [];
  //     return Object.entries(d).reduce((acc, [key, value]) => {
  //       if (validKeys.includes(key)) {
  //         const innerCodec = (c.props as any)[key];
  //         acc[key] = clean(value, innerCodec);
  //       }
  //       return acc;
  //     }, {} as Record<string,unknown>);
  //   }
  //   return d;
  // }

  const applyExact = (c: any, d: any) =>
    isArrayCodec(c) ? t.array(t.exact(c.type)).decode(d) : t.exact(c).decode(d);
  return applyExact(codec, clean(data, codec));
};

type PureNetworkQuery<Response> = {
  service?: string;
  pathname?: string;
  key?: string;
};

type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export type Query<EncodedType, ErrorType> = Omit<
  PartialBy<
    PureNetworkQuery<Response> & {
      codec: t.Type<EncodedType>;
      errorCodec?: t.Type<ErrorType>;
      formatError?: (error: ErrorType) => string;
      body?: unknown;
    },
    "key"
  >,
  "headers"
> & {
  method?: string;
  headers?: Record<string, string>;
  ignoreDefaultHeaders?: boolean;
};

const defaultQueryParams = {
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
};

const fetchApi = <EncodedType = unknown, ErrorType = unknown>({
  codec,
  ignoreDefaultHeaders = false,
  ...configParams
}: Query<EncodedType, ErrorType>) => {
  const environment = process.env.REACT_APP_ENVIRONMENT || "development";
  const service = configParams?.service || "core-service";
  const url = getServiceUrl({
    service,
    pathname: configParams?.pathname,
    environment,
  });
  let headers = configParams.headers;

  if (!ignoreDefaultHeaders) {
    headers = Object.fromEntries(
      Object.entries({
        ...defaultQueryParams.headers,
        ...configParams.headers,
      }).map(([k, v]) => [k.toLowerCase(), v])
    );
  }

  const options: RequestInit = {
    method: configParams.method || "GET",
    headers: new Headers(headers),
  };

  if (configParams.body instanceof FormData) {
    options.body = configParams.body;
  } else if (configParams.body && typeof configParams.body === "object") {
    if (options.method !== "GET" && configParams.body) {
      options.body = JSON.stringify(configParams.body);
    }
  }
  return fetchState(url, options)
    .then((x: { body: any; status: number }) => {
      return {
        body: x.body.responseData,
        status: x.body.responseCode || x.status,
        msg: x.body.message,
      } as { body: any; status: number; msg: string };
    })
    .then(async (x: { body: any; status: number; msg: string }) => {
      if (isYantrixApiError(x.body)) {
        throw new Error(formatYantrixApiError(x.body));
      } else if (x.status !== 200) {
        throw new Error(`Network error: ${x.msg}, status: ${x.status}`);
      } else {
        const encoded = await exactDecode(codec, x.body);
        switch (encoded._tag) {
          case "Left":
            const ioTsErrors = encoded.left;
            const message = [
              "Encoding failed: API Returned invalid response.",
              formatIoTsErrors("object", ioTsErrors),
            ].join("\n");
            throw new Error(message);
          case "Right":
            return encoded.right;
        }
      }
    })
    .then((x) => x)
    .catch((error: Error) => {
      throw new Error(`Network error: ${error}`);
    });
};

export default fetchApi;

export const getServiceUrl = ({
  service,
  pathname,
  environment,
}: {
  service?: string;
  pathname?: string;
  environment?: string;
}) => {
  if (pathname) {
    const host = selectHost(service, environment);
    if (host) {
      return `${host.protocol}//${host.hostname}${
        typeof host.port === undefined ? "" : `:${host.port}`
      }${pathname}`;
    }
  }
  return "http://localhost:3001";
};

interface FetchOptions extends RequestInit {
  customHeader?: string;
  retryCount?: number;
  body?: any;
}
async function fetchState<T>(
  url: string,
  options?: FetchOptions
): Promise<{ body: T; status: number }> {
  const response = await fetch(url, options);
  const body = await response.json();
  return { body, status: response.status };
}
